import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [error, setError] = useState("");

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const [unverifiedEmail, setUnverifiedEmail] = useState("");

  const login = useCallback(async (email, password) => {
    setError("");
    setUnverifiedEmail("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) tokenStore.set(data.token);
      setUser(data);
      return true;
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.code === "email_unverified") {
        setUnverifiedEmail(e.response.data.email || email);
      }
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const register = useCallback(async (email, password, name, inviteCode, acceptTerms = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, name, invite_code: inviteCode, accept_terms: acceptTerms, origin: window.location.origin });
      if (data.pending_verification) return { pending: true, email: data.email, message: data.message };
      if (data.token) tokenStore.set(data.token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const registerTrial = useCallback(async (email, password, name, acceptTerms = false) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register-trial", { email, password, name, accept_terms: acceptTerms, origin: window.location.origin });
      if (data.pending_verification) return { pending: true, email: data.email, message: data.message };
      if (data.token) tokenStore.set(data.token);
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Server-side session invalidation must not block the client-side logout.
      // Dev-only diagnostic; production build strips this branch via NODE_ENV.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed:", e?.message || e);
      }
    }
    tokenStore.clear();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading: user === null, error, unverifiedEmail, login, register, registerTrial, logout, refreshMe: bootstrap, setUser }),
    [user, error, unverifiedEmail, login, register, registerTrial, logout, bootstrap]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
